<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  FormDefinition,
  FormElementBase,
  FormElementConditionType,
  FormElementRule,
  FormElementRuleCondition,
  FormElementRuleType,
  FormElementType,
  RuleConditionBase,
  RuleConditionType,
} from "~/api";
import { conditionDisplayNames } from "~/utils/enumDisplayNames";
import { showError } from "~/utils/handleAPIError";
import { isFormElementBasedCondition } from "~/utils/typeGuards";

export interface EditCondition {
  rule: FormElementRule;
  condition: RuleConditionBase;
}

const emit = defineEmits<{
  (e: "update:rules", value: FormElementRule[]): void;
  (e: "update:editing-condition", value?: EditCondition): void;
}>();

const props = defineProps<{
  form: FormDefinition;
  editingCondition?: EditCondition;
}>();

/** Adding a Rule */
/** the list of form elements selected for a rule to be applied to */
const ruleConditionType = RuleConditionType.FORM_ELEMENT_BASED;

const _applicableElementGuids = ref<string[]>([]);
const applicableElements = computed<FormElementBase[]>(() =>
  props.form.components.filter((c) =>
    _applicableElementGuids.value.includes(c.guid),
  ),
);

/** the form element which's answer the rule depends on */
const _conditionElementGuid = ref<string>();
const conditionElement = computed<FormElementBase | undefined>(() =>
  props.form.components.find((c) => c.guid === _conditionElementGuid.value),
);

/** the type of the rule (e.g. hide, readonly) */
const ruleType = ref<FormElementRuleType>();
/** the type of the condition (e.g. answer equals) */
const conditionType = ref<FormElementConditionType>(
  Object.values(FormElementConditionType)[0],
);
/** the value for which this rule will be applied */
const answerValue = ref<string | null>();

/** whether to use "and" or "or" logic for rules with multiple conditions */
const trueIfAny = ref<boolean>(true);

/** available form items */
const formItems = computed<FormElementBase[]>(() =>
  props.form.components.filter((c) => c.guid !== undefined && !c.isArchived),
);

const formItemsWithoutAnswers = [
  FormElementType.CHANGE_STATUS_BUTTON,
  FormElementType.HEADING,
  FormElementType.FORM_TEXT,
  FormElementType.SECTION,
  FormElementType.DIVIDER,
  FormElementType.REPEATER,
  FormElementType.LINKED_ANSWER,
];

const nameStampOptions = [
  FormElementConditionType.ANSWER_HAS_ANY_VALUE,
  FormElementConditionType.ANSWER_HAS_NO_VALUE,
];

const fileUploadOption = [
  FormElementConditionType.COUNT_EQUALS,
  FormElementConditionType.COUNT_NOT_EQUALS,
];

const formItemsWithAnswers = computed(() =>
  formItems.value.filter((i) => !formItemsWithoutAnswers.includes(i.type)),
);

/** 'true' if the answer value is not required for the condition */
const isAnswerValueRequired = computed<boolean>(
  () =>
    conditionType.value !== FormElementConditionType.ANSWER_HAS_NO_VALUE &&
    conditionType.value !== FormElementConditionType.ANSWER_HAS_ANY_VALUE,
);

const numericElementTypes = [FormElementType.NUMBER, FormElementType.MONEY];

const showZeroInfo = computed<boolean>(
  () =>
    numericElementTypes.includes(
      conditionElement.value?.type as FormElementType,
    ) &&
    (conditionType.value === FormElementConditionType.ANSWER_HAS_NO_VALUE ||
      conditionType.value === FormElementConditionType.ANSWER_HAS_ANY_VALUE),
);

const formatAnswerValue = (answerValue: string) => {
  if (
    conditionElement.value?.type === FormElementType.CASCADER &&
    answerValue
  ) {
    const formattedValue = answerValue
      .replace(/\/{2,}/g, "/") // Replace multiple slashes with a single slash
      .replace(/\s*\/\s*/g, " / ") // Ensure consistent spacing around slashes
      .replace(/^\/|\/$/g, "") // Remove leading or trailing slashes
      .trim(); // Remove any remaining leading/trailing whitespace
    return formattedValue;
  }
  return answerValue;
};

/** 'true' if the form item is not required for the condition */
const isRuleReadyToAdd = computed<boolean>(() =>
  Boolean(
    applicableElements.value &&
      ruleType.value &&
      conditionElement.value &&
      conditionType.value &&
      (answerValue.value || !isAnswerValueRequired.value),
  ),
);

/**
 * add a rule to the form
 * @param elementQuestionCode the question code of the element to add the rule to
 */
const addElementRuleToForm = (
  elementQuestionCode: string,
  elementGuid: string | undefined,
  newCondition: FormElementRuleCondition,
) => {
  if (!isRuleReadyToAdd.value) {
    showError(
      "Developer Error: tried to add a rule that is missing some required fields",
    );
    return;
  }
  if (!ruleType.value || !elementQuestionCode) {
    showError(
      "Developer Error: tried to add a rule that is missing some required fields 2",
    );
    return;
  }

  if (!isAnswerValueRequired.value) {
    newCondition.answerValue = null;
  } else {
    newCondition.answerValue = formatAnswerValue(
      newCondition.answerValue ?? "",
    );
  }

  // If we're editing an existing condition, find the original rule
  if (props.editingCondition) {
    // Find the original rule that contains the condition we're editing
    const originalRule = props.form.rules.find(
      (rule) => rule.guid === props.editingCondition?.rule.guid,
    );

    if (originalRule) {
      const updatedRule = {
        ...originalRule,
        elementQuestionCode: elementQuestionCode,
        elementGuid: elementGuid ?? "",
        ruleType: ruleType.value,
        trueIfAny: trueIfAny.value,
        conditions: [
          ...originalRule.conditions.filter(
            (c) => c.guid !== props.editingCondition?.condition.guid,
          ),
          newCondition,
        ],
      };

      emit("update:rules", [
        ...props.form.rules.filter((r) => r !== originalRule),
        updatedRule,
      ]);
      emit("update:editing-condition", undefined);
      return;
    }
  }

  // check if the rule already exists
  const existingRule = props.form.rules.find(
    (rule) =>
      rule.elementQuestionCode === elementQuestionCode &&
      rule.ruleType === ruleType.value,
  );

  if (existingRule) {
    // Add the condition to the existing rule
    const updatedExistingRule = {
      ...existingRule,
      conditions: [...existingRule.conditions, newCondition],
      trueIfAny: trueIfAny.value,
    };
    emit("update:rules", [
      ...props.form.rules.filter((r) => r !== existingRule),
      updatedExistingRule,
    ]);
    return;
  }

  // otherwise, create a new rule
  const newRule: FormElementRule = {
    // @ts-expect-error: Force the server to generate the Guid
    guid: undefined,
    elementQuestionCode: elementQuestionCode,
    elementGuid: elementGuid ?? "",
    ruleType: ruleType.value,
    trueIfAny: trueIfAny.value,
    conditions: [newCondition],
  };

  emit("update:rules", [...props.form.rules, newRule]);
};

const resetFormFields = () => {
  conditionType.value = FormElementConditionType.ANSWER_EQUALS;
  _conditionElementGuid.value = undefined;
  answerValue.value = undefined;
  ruleType.value = undefined;
  _applicableElementGuids.value = [];
  trueIfAny.value = true;
};

/**
 * iterates through the list of question codes selected and adds the rule to each element
 */
const handleAddRule = () => {
  if (!isRuleReadyToAdd.value) {
    showError(
      "Developer Error: tried to add a rule that is missing some required fields",
    );
    return;
  }

  const newCondition: FormElementRuleCondition = {
    // @ts-expect-error: Force the server to generate the Guid
    guid: undefined,
    conditionType: ruleConditionType,
    type: conditionType.value,
    questionGuid: conditionElement.value?.guid,
    questionCode: conditionElement.value?.questionCode,
    answerValue: formatAnswerValue(answerValue.value ?? ""),
  };

  // iterate through the list of element guids selected and add the rule to each element
  applicableElements.value.forEach((el) =>
    addElementRuleToForm(el.questionCode, el.guid, newCondition),
  );
  resetFormFields();
};

watch(
  () => _conditionElementGuid.value,
  () => {
    if (conditionElement.value?.type === FormElementType.NAME_STAMP) {
      conditionType.value = nameStampOptions[0];
    } else if (conditionElement.value?.type === FormElementType.FILE_UPLOAD) {
      conditionType.value = fileUploadOption[0];
    } else {
      if (
        conditionType.value === FormElementConditionType.COUNT_EQUALS ||
        conditionType.value === FormElementConditionType.COUNT_NOT_EQUALS
      ) {
        conditionType.value = FormElementConditionType.ANSWER_EQUALS;
      }
    }
  },
);

watch(
  () => props.editingCondition,
  () => {
    if (
      props.editingCondition &&
      isFormElementBasedCondition(props.editingCondition.condition)
    ) {
      _conditionElementGuid.value =
        props.editingCondition.condition.questionGuid ?? undefined;
      conditionType.value = props.editingCondition.condition.type;
      answerValue.value = props.editingCondition.condition.answerValue;
      ruleType.value = props.editingCondition.rule.ruleType;
      _applicableElementGuids.value = [props.editingCondition.rule.elementGuid];
      trueIfAny.value = props.editingCondition.rule.trueIfAny;
    } else {
      _conditionElementGuid.value = undefined;
      conditionType.value = FormElementConditionType.ANSWER_EQUALS;
      answerValue.value = undefined;
      ruleType.value = undefined;
      _applicableElementGuids.value = [];
      trueIfAny.value = true;
    }
  },
);
</script>

<template>
  <div class="rule-editor">
    If

    <el-select
      v-model="_conditionElementGuid"
      size="small"
      placeholder="Form Item"
      clearable
      style="max-width: 180px"
      filterable
    >
      <el-option
        v-for="item in formItemsWithAnswers"
        :key="item.questionCode"
        :label="item.questionCode"
        :value="item.guid"
      >
      </el-option>
    </el-select>

    <el-select
      v-model="conditionType"
      size="small"
      placeholder="Condition"
      clearable
      style="max-width: 200px"
      filterable
    >
      <el-option
        v-for="condition in conditionElement?.type ===
        FormElementType.NAME_STAMP
          ? nameStampOptions
          : conditionElement?.type === FormElementType.FILE_UPLOAD
            ? fileUploadOption
            : Object.values(FormElementConditionType).filter(
                (c) =>
                  c !== FormElementConditionType.COUNT_EQUALS &&
                  c !== FormElementConditionType.COUNT_NOT_EQUALS,
              )"
        :key="condition"
        :label="conditionDisplayNames[condition]"
        :value="condition"
      >
      </el-option>
    </el-select>

    <el-input
      v-if="isAnswerValueRequired"
      v-model="answerValue"
      class="answer"
      size="small"
      placeholder="Value"
      clearable
    />

    Set
    <el-select
      v-if="!editingCondition"
      v-model="_applicableElementGuids"
      size="small"
      placeholder="Form Item(s)"
      multiple
      collapse-tags
      collapse-tags-tooltip
      filterable
      clearable
    >
      <el-option
        v-for="item in formItems.filter(
          (x) => x.questionCode !== conditionElement?.questionCode,
        )"
        :key="item.guid"
        :label="item.questionCode"
        :value="item.guid"
      >
      </el-option>
    </el-select>
    <el-select
      v-if="editingCondition"
      v-model="_applicableElementGuids"
      size="small"
      placeholder="Form Item(s)"
      collapse-tags
      collapse-tags-tooltip
      filterable
      clearable
    >
      <el-option
        v-for="item in formItems.filter(
          (x) => x.questionCode !== conditionElement?.questionCode,
        )"
        :key="item.guid"
        :label="item.questionCode"
        :value="item.guid"
      >
      </el-option>
    </el-select>

    to

    <el-select
      v-model="ruleType"
      size="small"
      placeholder="Rule Type"
      filterable
      clearable
    >
      <el-option
        v-for="rt in Object.values(FormElementRuleType)"
        :key="rt"
        :label="rt"
        :value="rt"
      >
      </el-option>
    </el-select>

    <el-checkbox v-model="trueIfAny" size="small" label="True if any?" />
    <el-button
      v-if="!editingCondition"
      :disabled="!isRuleReadyToAdd"
      type="primary"
      @click="handleAddRule"
    >
      Add
    </el-button>
    <div v-else style="display: flex">
      <el-button
        type="primary"
        :disabled="!isRuleReadyToAdd"
        @click="handleAddRule"
      >
        Update
      </el-button>

      <el-button
        type="danger"
        plain
        @click="emit('update:editing-condition', undefined)"
      >
        Cancel
      </el-button>
    </div>
  </div>
  <el-alert
    v-if="showZeroInfo"
    type="info"
    show-icon
    :closable="false"
    title='A value of 0 is treated as "no value" for this condition.'
    style="margin-top: 8px"
  />
</template>

<style lang="less" scoped>
.rule-editor {
  display: flex;
  gap: 20px;
  align-items: center;
  .answer {
    height: fit-content;
  }
}
</style>
